import * as baseConfig from '@/config/base-config.json';
import * as config from '@/config/config.json';
import { GST_DEFAULT, SSD_LIST, SSD_OPTIONS } from '@/constants/common';
import { formatCurrency } from '@/utils';
import { generateSliderSteps } from '@/utils/generateSliderSteps';

export const MAX_PROFILES = 4;

export const HDB_SALES_PROCEEDS_LABEL = {
  // Sales Information
  HDBSaleProceedsSalesInformationLabel: 'Sales Information',
  HDBSaleProceedsEstimateSellingPriceLabel: 'Estimated Sale Price',
  HDBSaleProceedsOutstandingLoanLabel: 'Outstanding Loan',
  HDBSaleProceedsOptionFeeLabel: 'Option Fee',
  HDBSaleProceedsOptionFeeRangeLabel: '($1 - $1,000)',
  HDBSaleProceedsOptionExerciseFeeLabel: 'Option Exercise Fee',
  HDBSaleProceedsCPFUsedForHousingLabel: 'CPF Used for Housing',
  HDBSaleProceedsCPFAccruedInterestLabel: 'CPF Accrued Interest',

  // Next Housing Plans
  HDBSaleProceedsNextHousingPlansLabel: 'Next Housing Plans',
  HDBSaleProceedsNextHousingPlansRangeLabel: 'Are you purchasing another HDB flat?',
  HDBSaleProceedsSecondHDBLoanLabel: 'Will this be your second HDB Loan?',

  // Outstanding HDB Payments
  HDBSaleProceedsOutstandingHDBPaymentsTitleLabel: 'Outstanding HDB Payments',
  HDBSaleProceedsResaleLevyLabel: 'Resale Levy',
  HDBSaleProceedsResaleLevyPayableLabel: 'Resale Levy Payable',
  HDBSaleProceedsUpgradingCostLabel: 'Upgrading Cost',
  HDBSaleProceedsUpgradingCostPayableLabel: 'Upgrading Cost Payable',

  // Associated Payments
  HDBSaleProceedsAssociatedPaymentsTitleLabel: 'Associated Payments',
  HDBSaleProceedsSSDLabel: "Seller's Stamp Duty",
  HDBSaleProceedsSSDNote:
    'For SERS replacement flats, SSD may still apply even after MOP is fulfilled. Holding period is computed from the executed Agreement for Lease date. Please verify with IRAS if unsure.',
  HDBSaleProceedsLegalFeeLabel: 'Legal Fees',
  HDBSaleProceedsResaleApplicationFeeLabel: 'Resale Application Fee',
  HDBSaleProceedsServiceCommissionLabel: 'Service Commission',
  HDBSaleProceedsOthersLabel: 'Others',

  // Summary
  HDBSaleProceedsSummaryTitleLabel: 'Summary',
  HDBSaleProceedsDeductiblesLabel: 'Deductibles',
  HDBSaleProceedsUseForNextHDBFlatLabel: 'Use for Next HDB Flat',
  HDBSaleProceedsEstimateCashProceedsLabel: 'Estimated Cash Proceeds',

  // Deductibles Breakdown
  HDBSaleProceedsDeductiblesBreakdownTitleLabel: 'Deductibles Breakdown',
  HDBSaleProceedsCPFUsedPlusInterestLabel: 'CPF Used & Interest',
  HDBSaleProceedsTotalDeductiblesLabel: 'Total Deductibles',
  HDBSaleProceedsDeductiblesNoteLabel:
    "Please note that these figures are indicative and may be different from the final amounts. Sellers may also need to pay other outstanding fees eg HDB's penalty fee, outstanding S&CC (Service & Conservancy Charges), outstanding tax before resale completion.",

  // Sales Proceeds Timeline
  HDBSaleProceedsSalesProceedsTimelineTitleLabel: 'Sales Proceeds Timeline',

  HDBSaleProceedsOTPLongFormLabel: 'Option to Purchase',
  HDBSaleProceedsOTPLabel: 'OTP',
  HDBSaleProceedsExercisePeriodDaysLabel: 'Exercise Period',
  HDBSaleProceedsSubmitOfResaleApplicationDaysLabel: 'Submit of Resale Application',
  HDBSaleProceedsAcceptanceOfResaleApplicationLabel: 'Acceptance of Resale Application',
  HDBSaleProceedsWorkingDaysLabel: 'Working Days',
  HDBSaleProceedsTemporaryExtensionOfStayMonthsLabel: 'Temporary Extension of Stay',

  // Additional UI Elements
  HDBSaleProceedsWeeksLabel: 'Weeks',
  HDBSaleProceedsDaysLabel: 'Days',
  HDBSaleProceedsMonthsLabel: 'Months',
  HDBSaleProceedsPlusAddProfileLabel: '+ Add Profile',
  HDBSaleProceedsAdditionalProfileLabel: 'Additional Profile',
  HDBSaleProceedsExerciseOfOTPLabel: 'Exercise of OTP',
  HDBSaleProceedsSalesCompletionLabel: 'Sales Completion',
  HDBSaleProceedsBalanceLabel: 'Balance',
  HDBSaleProceedsOptionExerciseFeeIncludedLabel: 'Option Exercise Fee Included',
  HDBSaleProceedsAgentFeeLabel: 'Agent Fee',
  HDBSaleProceedsAmountLabel: 'Amount',
  HDBSaleProceedsActualHandoverLabel: 'Actual Handover',
  HDBSaleProceedsGenerateTimelineLabel: 'Generate Timeline',
};

export const OPTION_FEE = {
  DEFAULT: baseConfig.option_fee_cash.default,
  RANGE: generateSliderSteps(baseConfig.option_fee_cash.min, baseConfig.option_fee_cash.max),
  MIN: baseConfig.option_fee_cash.min,
  MAX: baseConfig.option_fee_cash.max,
};

export const OPTION_EXERCISE_FEE = {
  DEFAULT: baseConfig.option_exercise_fee_cash.default,
  MIN: baseConfig.option_exercise_fee_cash.min,
  MAX: baseConfig.option_exercise_fee_cash.max,
};

export const OPTION_FEE_AND_EXERCISE_FEE_MAX = 5000;

export const SSD = {
  DEFAULT: 0,
  LIST: SSD_OPTIONS,
};

export const LEGAL_FEE = {
  DEFAULT: baseConfig.legal_fee,
};

export const RESALE_APPLICATION_FEE = {
  LIST: [
    {
      value: config.resale_application_fee.option_1.toString(),
      label: `${formatCurrency(config.resale_application_fee.option_1)}`,
    },
    {
      value: config.resale_application_fee.option_2.toString(),
      label: `${formatCurrency(config.resale_application_fee.option_2)}`,
    },
  ],
};
export const EXERCISE_PERIOD = {
  DEFAULT: baseConfig.exercise_period_in_days.default,
  MIN: baseConfig.exercise_period_in_days.min,
  MAX: baseConfig.exercise_period_in_days.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    baseConfig.exercise_period_in_days.min,
    baseConfig.exercise_period_in_days.max,
  ),
};

export const COMPLETION_PERIOD = {
  DEFAULT: baseConfig.completion_period_in_weeks.default,
  MIN: baseConfig.completion_period_in_weeks.min,
  MAX: baseConfig.completion_period_in_weeks.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    baseConfig.completion_period_in_weeks.min,
    baseConfig.completion_period_in_weeks.max,
  ),
};

export const SUBMIT_RESALE_APPLICATION = {
  DEFAULT: baseConfig.submit_resale_application_in_days.default,
  MIN: baseConfig.submit_resale_application_in_days.min,
  MAX: baseConfig.submit_resale_application_in_days.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    baseConfig.submit_resale_application_in_days.min,
    baseConfig.submit_resale_application_in_days.max,
  ),
};

export const ACCEPTANCE_RESALE_APPLICATION = {
  DEFAULT: baseConfig.acceptance_resale_application_in_days.default,
  MIN: baseConfig.acceptance_resale_application_in_days.min,
  MAX: baseConfig.acceptance_resale_application_in_days.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    baseConfig.acceptance_resale_application_in_days.min,
    baseConfig.acceptance_resale_application_in_days.max,
  ),
};

export const TEMPORARY_EXTENSION_OF_STAY = {
  DEFAULT: baseConfig.temporary_extension_stay_in_months.default,
  MIN: baseConfig.temporary_extension_stay_in_months.min,
  MAX: baseConfig.temporary_extension_stay_in_months.max,
  STEP: 0.5,
  LABEL_INPUTS: generateSliderSteps(
    baseConfig.temporary_extension_stay_in_months.min,
    baseConfig.temporary_extension_stay_in_months.max,
    0.5,
  ),
};

export const SERVICE_COMMISSION_PERCENT = baseConfig.service_commission_in_percent;

export const GST_PERCENT = GST_DEFAULT;

// Timeline Configuration
export const HDB_RESALE_COMPLETION_PERIOD_IN_DAYS = baseConfig.hdb_resale_completion_period_in_days;
export const HDB_APPROVAL_LETTER_PERIOD_IN_DAYS = baseConfig.hdb_approval_letter_period_in_days;
export const TEMPORARY_EXTENSION_START_DELAY_IN_DAYS =
  baseConfig.temporary_extension_start_delay_in_days;

export const RESALE_LEVY_AMOUNTS = {
  '2-room': {
    married: config.resale_levy_amounts['2_room'].married,
    single: config.resale_levy_amounts['2_room'].single,
  },
  '3-room': {
    married: config.resale_levy_amounts['3_room'].married,
    single: config.resale_levy_amounts['3_room'].single,
  },
  '4-room': {
    married: config.resale_levy_amounts['4_room'].married,
    single: config.resale_levy_amounts['4_room'].single,
  },
  '5-room': {
    married: config.resale_levy_amounts['5_room'].married,
    single: config.resale_levy_amounts['5_room'].single,
  },
  executive: {
    married: config.resale_levy_amounts.executive.married,
    single: config.resale_levy_amounts.executive.single,
  },
  ec: {
    married: config.resale_levy_amounts.ec.married,
    single: config.resale_levy_amounts.ec.single,
  },
} as const;

// Define the flat types as enum
export enum ResaleLevyFlatType {
  TWO_ROOM = '2-room',
  THREE_ROOM = '3-room',
  FOUR_ROOM = '4-room',
  FIVE_ROOM = '5-room',
  EXECUTIVE = 'executive',
  EC = 'ec',
}

// Define marital status types
export enum ResaleLevyMaritalStatus {
  MARRIED = 'married',
  SINGLE = 'single',
}

// Label mapping
export const FLAT_TYPE_LABELS = {
  [ResaleLevyFlatType.TWO_ROOM]: '2-Room Flat',
  [ResaleLevyFlatType.THREE_ROOM]: '3-Room Flat',
  [ResaleLevyFlatType.FOUR_ROOM]: '4-Room Flat',
  [ResaleLevyFlatType.FIVE_ROOM]: '5-Room Flat',
  [ResaleLevyFlatType.EXECUTIVE]: 'Executive',
  [ResaleLevyFlatType.EC]: 'E.C.',
} as const;

// Function to get the label for a resale levy type
export const getResaleLevyTypeLabel = (resaleLevyType: string): string => {
  // Handle single/divorce types
  if (resaleLevyType.startsWith('single-')) {
    const flatType = resaleLevyType.replace('single-', '') as ResaleLevyFlatType;

    return FLAT_TYPE_LABELS[flatType] || '';
  }

  // Handle married types
  const flatType = resaleLevyType as ResaleLevyFlatType;

  return FLAT_TYPE_LABELS[flatType] || '';
};

// Generate options programmatically (following codebase pattern)
export const RESALE_LEVY_OPTIONS = [
  {
    label: 'Family Resale Levy - Type',
    options: Object.values(ResaleLevyFlatType).map((value) => ({
      label: getResaleLevyTypeLabel(value),
      value,
    })),
  },
  {
    label: 'Single/Divorce Resale Levy - Type',
    options: Object.values(ResaleLevyFlatType).map((value) => ({
      label: getResaleLevyTypeLabel(`single-${value}`),
      value: `single-${value}`,
    })),
  },
];

export const HDB_WEBSITE_LINK_TEXT = 'Click for reference to the HDB website';
export const RESALE_LEVY_CONTENT = {
  TITLE: 'Resale Levy',
  DESCRIPTION:
    'The resale levy is a fixed sum payable to HDB in cash and/ or flat sale proceeds when flat owners buy a second subsidised flat from HDB. The levy payable depends on the flat type of their first subsidised flat.',
  HDB_WEBSITE_LINK:
    'https://www.hdb.gov.sg/residential/buying-a-flat/conditions-after-buying#Resalelevy-4',
  HDB_WEBSITE_LINK_TEXT,
} as const;

export const UPGRADING_COST_CONTENT = {
  TITLE: 'Upgrading Cost',
  DESCRIPTION:
    'If the upgrading invoice is issued before resale completion, the seller must pay the full outstanding upgrading cost (including any accrued interest) before the  resale transaction can be completed.',
  HDB_WEBSITE_LINK: 'https://services2.hdb.gov.sg/webapp/BB33RESLSTATUS/BB33SEnquiry',
  HDB_WEBSITE_LINK_TEXT: 'Click to check the status of the upgrading programme.',
} as const;

export const OTP_CONTENT = {
  TITLE: 'Option to Purchase (OTP)',
  DESCRIPTION:
    'Option to Purchase (OTP) granted after accepting offer, submission timeline, Extension of Stay & other Terms is agreed.',
} as const;

export const EXERCISE_PERIOD_CONTENT = {
  TITLE: 'Exercise Period (Days)',
  DESCRIPTION:
    'OTP to be exercised within 21 days. Ensure Bank Letter of Offer is in order before Exercise. For clients taking HFE, HFE have to be granted before clients are able to be granted OTP.',
} as const;

export const SUBMIT_RESALE_APPLICATION_CONTENT = {
  TITLE: 'Submit of Resale Application (Days)',
  DESCRIPTION:
    'General rule of thumb, Submission of Resale Application should be within a maximum of 90 days.',
} as const;

export const ACCEPTANCE_RESALE_APPLICATION_CONTENT = {
  TITLE: 'Acceptance of Resale Application (Working Days)',
  DESCRIPTION:
    "To adjust the date please note, HDB's acceptance usually falls within 28 working days.",
  HDB_WEBSITE_LINK:
    'https://www.hdb.gov.sg/residential/selling-a-flat/resale-application/application#Aftersubmissionofresaleapplication-4',
  HDB_WEBSITE_LINK_TEXT,
} as const;

export const TEMPORARY_EXTENSION_OF_STAY_CONTENT = {
  TITLE: 'Temporary Extension of Stay (Months)',
  DESCRIPTION:
    'A private arrangement between flat sellers and buyer that allows sellers who have bought another property more time to move out of their flat, after resale completion.',
  HDB_WEBSITE_LINK:
    'https://www.hdb.gov.sg/residential/selling-a-flat/resale-application/request-for-temporary-extension-of-stay',
  HDB_WEBSITE_LINK_TEXT,
} as const;

export const CASH_PROCEEDS_FOR_NEXT_HDB_FLAT_CONTENT = {
  TITLE: 'Use for Next HDB Flat',
  DESCRIPTION:
    'Generally, flat applicants can keep the greater of $25,000 or 50% of the cash proceeds (including the cash deposit received). HDB will take into account the remaining cash proceeds when determining the second HDB housing loan amount.',
  HDB_WEBSITE_LINK:
    'https://www.hdb.gov.sg/cs/infoweb/residential/buying-a-flat/understanding-your-eligibility-and-housing-loan-options/housing-loan-options/housing-loan-from-hdb',
  HDB_WEBSITE_LINK_TEXT,
} as const;
